import { caseTemplateSchema } from "@/components/Forms/schema/caseTemplateSchema.ts";
import { taskTemplateSchema } from "@/components/Forms/schema/taskTemplateSchema.ts";
import useAuthenticatedFetch from "@/hooks/useAuthenticatedFetch.ts";
import { apiUrl } from "@/utils/api.ts";
import { type UseQueryResult, useQuery } from "@tanstack/react-query";
import { createDataSelector, createResourceCollectionSelector } from "jsonapi-zod-query";
import type {
    MRT_ColumnFiltersState,
    MRT_PaginationState,
    MRT_SortingState,
} from "material-react-table";

const resourceSelector = createResourceCollectionSelector({
    type: "caseTemplate",
    attributesSchema: caseTemplateSchema,
    relationships: {
        taskTemplates: {
            relationshipType: "many",
            include: {
                type: "taskTemplate",
                attributesSchema: taskTemplateSchema,
            },
            required: false,
        },
    },
});

const caseTemplatesSelector = createDataSelector(resourceSelector);
export type CaseTemplates = ReturnType<typeof caseTemplatesSelector>;
export type MrtCaseTemplate = CaseTemplates[number];

type Props = {
    isQueryEnabled?: boolean;
    columnFilters: MRT_ColumnFiltersState;
    globalFilter: string;
    pagination: MRT_PaginationState;
    sorting: MRT_SortingState;
};

export const useFetchMrtTemplates = ({
    isQueryEnabled = true,
    columnFilters,
    globalFilter,
    pagination,
    sorting,
}: Props): UseQueryResult<CaseTemplates> => {
    const fetch = useAuthenticatedFetch();

    return useQuery({
        refetchOnWindowFocus: false,
        queryKey: [
            "case-templates",
            columnFilters,
            globalFilter,
            pagination.pageIndex,
            pagination.pageSize,
            sorting,
        ],
        enabled: isQueryEnabled,
        queryFn: async () => {
            const requestBody = {
                start: pagination.pageIndex * pagination.pageSize,
                size: pagination.pageSize,
                filters: columnFilters ?? [],
                globalFilter: globalFilter ?? "",
                sorting: sorting ?? [],
            };

            const response = await fetch(apiUrl("/case-template"), {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(requestBody),
            });

            return await response.json();
        },
        select: caseTemplatesSelector,
    });
};
